import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Camera, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  X,
  RotateCcw 
} from 'lucide-react';

interface PhotoProofCaptureProps {
  onPhotoTaken: (photoUrl: string) => void;
  onSkip: () => void;
  action: 'clockin' | 'clockout' | 'addphoto';
  title?: string;
  subtitle?: string;
}

export function PhotoProofCapture({ onPhotoTaken, onSkip, action }: PhotoProofCaptureProps) {
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Check camera permission on mount and auto-start if allowed
  useEffect(() => {
    const initializeCamera = async () => {
      await checkCameraPermission();
      // If permission is already granted, we could auto-start the camera
      // but let's keep it manual for better UX
    };
    
    initializeCamera();
    
    return () => {
      // Cleanup stream on unmount
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const checkCameraPermission = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Camera not supported on this device');
        setHasPermission(false);
        return;
      }

      // Check if we have permission
      const permissionStatus = await navigator.permissions?.query({ name: 'camera' as PermissionName });
      if (permissionStatus) {
        setHasPermission(permissionStatus.state === 'granted');
        permissionStatus.onchange = () => {
          setHasPermission(permissionStatus.state === 'granted');
        };
      }
    } catch (error) {
      console.log('Permission API not supported, will check on camera access');
      setHasPermission(null);
    }
  };

  const startCamera = async () => {
    try {
      setIsCapturing(true);
      setCameraError(null);
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Camera timeout')), 10000);
      });
      
      const cameraPromise = navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user', // Front camera for selfie
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      const mediaStream = await Promise.race([cameraPromise, timeoutPromise]) as MediaStream;
      
      setStream(mediaStream);
      setHasPermission(true);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error: any) {
      console.error('Error accessing camera:', error);
      setIsCapturing(false);
      
      if (error.name === 'NotAllowedError') {
        setCameraError('Camera permission was denied. Click "Allow" when your browser asks for camera access.');
        setHasPermission(false);
      } else if (error.name === 'NotFoundError') {
        setCameraError('No camera found on this device. You can still continue without a photo.');
      } else if (error.name === 'NotSupportedError') {
        setCameraError('Camera not supported on this device. You can continue without a photo.');
      } else if (error.message === 'Camera timeout') {
        setCameraError('Camera is taking too long to respond. Please refresh the page and try again.');
      } else {
        setCameraError('Unable to access camera. Check your browser settings and try again.');
      }
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (context) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      
      const photoUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(photoUrl);
      
      // Stop camera
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    startCamera();
  };

  const confirmPhoto = () => {
    if (capturedPhoto) {
      onPhotoTaken(capturedPhoto);
    }
  };

  const handleSkip = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    onSkip();
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-white/10 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-lg font-semibold text-white">
                Photo Proof Required
              </h2>
              <p className="text-sm text-white/80">
{action === 'clockin' 
                  ? 'Take a selfie to clock in' 
                  : action === 'clockout' 
                  ? 'Take a selfie to clock out' 
                  : 'Add photo proof to strengthen your records'}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleSkip}
            className="text-white hover:bg-white/20"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Status Alert */}
      <div className="px-4 py-2">
        {cameraError ? (
          <Alert className="border-destructive bg-destructive/10">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <AlertDescription className="text-destructive">
              <strong>Camera access blocked.</strong> Please allow camera permission to take photos for the best protection.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-primary bg-primary/10">
            <Camera className="h-4 w-4 text-primary" />
            <AlertDescription className="text-primary">
              <strong>Your best protection!</strong> A quick photo gives you the strongest proof of your hours.
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Camera/Photo Display */}
      <div className="flex-1 flex items-center justify-center p-4">
        {cameraError && (
          <div className="text-center space-y-6 max-w-md mx-auto">
            <div className="w-32 h-32 bg-destructive/20 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="w-16 h-16 text-destructive" />
            </div>
            <div className="text-white space-y-3">
              <h3 className="text-xl font-semibold">Camera Permission Needed</h3>
              <p className="text-white/90 leading-relaxed">{cameraError}</p>
              {hasPermission === false && (
                <div className="bg-white/10 rounded-lg p-4 text-left space-y-2">
                  <p className="text-white/90 font-medium text-sm">How to enable camera access:</p>
                  <ol className="text-white/80 text-sm space-y-1 list-decimal list-inside">
                    <li>Look for the camera icon in your browser's address bar</li>
                    <li>Click on it and select "Allow"</li>
                    <li>Or go to your browser settings → Site permissions → Camera → Allow</li>
                    <li>Refresh this page and try again</li>
                  </ol>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <Button 
                onClick={startCamera}
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 w-full"
                size="lg"
              >
                <Camera className="w-5 h-5 mr-2" />
                Try Camera Again
              </Button>
              <Button 
                onClick={handleSkip}
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-full px-8 w-full"
                size="lg"
              >
                Continue Without Photo (Basic Protection)
              </Button>
            </div>
          </div>
        )}

        {!isCapturing && !capturedPhoto && !cameraError && (
          <div className="text-center space-y-6 max-w-md mx-auto">
            <div className="w-32 h-32 bg-primary/20 rounded-full flex items-center justify-center mx-auto">
              <Camera className="w-16 h-16 text-primary" />
            </div>
            <div className="text-white space-y-3">
              <h3 className="text-xl font-semibold">Let's Take Your Photo</h3>
              <p className="text-white/90 leading-relaxed max-w-sm mx-auto">
                A quick selfie shows you're at work when you {action === 'clockin' ? 'clock in' : 'clock out'}.
                This gives you the best protection for your hours.
              </p>
              <div className="bg-white/10 rounded-lg p-3 text-sm text-white/80">
                📱 Your browser will ask for camera permission - please click "Allow"
              </div>
            </div>
            <div className="space-y-3">
              <Button 
                onClick={startCamera}
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 w-full"
                size="lg"
              >
                <Camera className="w-5 h-5 mr-2" />
                Start Camera & Request Permission
              </Button>
              <p className="text-white/60 text-sm">
                Don't want to use camera?{' '}
                <button 
                  onClick={handleSkip}
                  className="text-white underline hover:text-white/80"
                >
                  Continue without photo
                </button>
              </p>
            </div>
          </div>
        )}

        {isCapturing && !capturedPhoto && (
          <div className="relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-80 h-60 object-cover rounded-lg"
            />
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <Button 
                onClick={capturePhoto}
                className="bg-white hover:bg-white/90 text-black rounded-full w-16 h-16"
                size="lg"
              >
                <Camera className="w-8 h-8" />
              </Button>
            </div>
          </div>
        )}

        {capturedPhoto && (
          <div className="text-center space-y-4">
            <img 
              src={capturedPhoto} 
              alt="Captured proof" 
              className="w-80 h-60 object-cover rounded-lg mx-auto"
            />
            <div className="flex space-x-4 justify-center">
              <Button 
                onClick={retakePhoto}
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Retake
              </Button>
              <Button 
                onClick={confirmPhoto}
                className="bg-success hover:bg-success/90 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Use This Photo
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="p-4 space-y-3">
        <div className="text-center">
          <Badge className="bg-success/20 text-success border-success/40 mb-2">
            <Shield className="w-3 h-3 mr-1" />
            Best Protection
          </Badge>
          <p className="text-sm text-white/70">
            Photos give you the strongest proof of your work hours
          </p>
        </div>
        
        <Button 
          onClick={handleSkip}
          variant="ghost"
          className="w-full text-white/60 hover:text-white hover:bg-white/10"
        >
{action === 'addphoto' ? 'Cancel' : 'Skip for Now (Basic Protection)'}
        </Button>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}